import { type EditedWorld, type Footprint, MOBS, TILE_SIZE } from '@shefira/shared';
import type Phaser from 'phaser';

/**
 * What the world editor draws over the world (ADR 0022): a marker on every
 * inhabitant's tile (green for the peaceful, red for monsters) with the
 * circle it wanders in, and the outlines of what is hovered and selected.
 * The inhabitants themselves are the server's and stay where they are until
 * it restarts; the marker shows where they will stand.
 */

/** Above every sprite of the world. */
const OVERLAY_DEPTH = 1_000_000;
const PEACEFUL = 0x9ece6a;
const HOSTILE = 0xf7768e;
const HOVER = 0xffffff;
const SELECTED = 0xffe08a;
const BROKEN = 0xff5555;

export class EditorOverlay {
  private readonly markers: Phaser.GameObjects.Graphics;
  private readonly outline: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene) {
    this.markers = scene.add.graphics().setDepth(OVERLAY_DEPTH).setVisible(false);
    this.outline = scene.add
      .graphics()
      .setDepth(OVERLAY_DEPTH + 1)
      .setVisible(false);
  }

  setVisible(visible: boolean): void {
    this.markers.setVisible(visible);
    this.outline.setVisible(visible);
  }

  drawSpawns(world: EditedWorld, selectedOrigin: string | undefined): void {
    this.markers.clear();
    for (const { den, origin } of world.spawns) {
      const colour = MOBS[den.mob].peaceful ? PEACEFUL : HOSTILE;
      if (den.radius > 0) {
        this.markers.lineStyle(1, colour, origin === selectedOrigin ? 0.8 : 0.25);
        this.markers.strokeCircle(
          (den.x + 0.5) * TILE_SIZE,
          (den.y + 0.5) * TILE_SIZE,
          (den.radius + 0.5) * TILE_SIZE,
        );
      }
      this.markers.fillStyle(colour, 0.45);
      this.markers.fillRect(
        den.x * TILE_SIZE + 4,
        den.y * TILE_SIZE + 4,
        TILE_SIZE - 8,
        TILE_SIZE - 8,
      );
    }
  }

  /** The hovered box in white, the selected one in gold, or red while the edits have problems. */
  drawOutline(
    hover: Footprint | undefined,
    selected: Footprint | undefined,
    broken: boolean,
  ): void {
    this.outline.clear();
    if (hover !== undefined) this.box(hover, HOVER);
    if (selected !== undefined) this.box(selected, broken ? BROKEN : SELECTED);
  }

  destroy(): void {
    this.markers.destroy();
    this.outline.destroy();
  }

  private box(box: Footprint, colour: number): void {
    this.outline.lineStyle(1, colour, 1);
    this.outline.strokeRect(
      box.x0 * TILE_SIZE + 0.5,
      box.y0 * TILE_SIZE + 0.5,
      (box.x1 - box.x0 + 1) * TILE_SIZE - 1,
      (box.y1 - box.y0 + 1) * TILE_SIZE - 1,
    );
  }
}
